// users.js
// Firebase users collection API functions

import { db, auth } from '../firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  setDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { deleteUser as deleteAuthUser } from 'firebase/auth';
import { generateMembershipID } from '../utils/generateMembershipID';
import { getNextMembershipNumber } from './auth';

/**
 * Generate membership IDs for users who don't have one
 * @returns {Promise<Object>} Result with count of updated users
 */
export const generateMissingMembershipIDs = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef);
    const snapshot = await getDocs(q);

    const usersWithoutID = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (!data.membershipID || data.membershipID === '' || data.membershipID === 'N/A') {
        usersWithoutID.push({ id: doc.id, ...data });
      }
    });

    if (usersWithoutID.length === 0) {
      return { success: true, count: 0, message: 'All users already have membership IDs' };
    }

    // Get starting number
    let nextNumber = await getNextMembershipNumber();

    // Use batch to update multiple documents efficiently
    const batch = writeBatch(db);

    usersWithoutID.forEach(user => {
      const userRef = doc(db, 'users', user.id);
      const membershipID = generateMembershipID('KOS', nextNumber);
      batch.update(userRef, {
        membershipID: membershipID,
        updatedAt: serverTimestamp()
      });
      nextNumber++;
    });

    await batch.commit();

    return {
      success: true,
      count: usersWithoutID.length,
      message: `Generated ${usersWithoutID.length} membership IDs`
    };
  } catch (error) {
    console.error('Error generating membership IDs:', error);
    throw error;
  }
};

/**
 * Get user profile by ID
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef);
    const querySnapshot = await getDocs(q);

    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });

    return users;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

/**
 * Delete user (admin only)
 */
export const deleteUser = async (userId) => {
  try {
    // Delete user document from Firestore
    await deleteDoc(doc(db, 'users', userId));

    // Note: Deleting from Firebase Auth requires admin SDK on backend
    // This only deletes the Firestore document
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Update user role (admin only)
 */
export const updateUserRole = async (userId, role) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: role,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Update user points (admin only)
 */
export const updateUserPoints = async (userId, points) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (points === undefined || points === null) {
      throw new Error('Points value is required');
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      points: points,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user points:', error);
    throw error;
  }
};

/**
 * Update user tier (admin only)
 */
export const updateUserTier = async (userId, tier) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (tier === undefined || tier === null) {
      throw new Error('Tier value is required');
    }
    if (tier < 1 || tier > 3) {
      throw new Error('Tier must be between 1 and 3');
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      tier: tier,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user tier:', error);
    throw error;
  }
};

/**
 * Search users by name or email
 */
export const searchUsers = async (searchTerm) => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);

    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = { id: doc.id, ...doc.data() };
      const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.toLowerCase();
      const email = (userData.email || '').toLowerCase();
      const search = searchTerm.toLowerCase();

      if (fullName.includes(search) || email.includes(search)) {
        users.push(userData);
      }
    });

    return users;
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Add a redeemed reward to user's profile
 */
export const addRedeemedReward = async (userId, rewardId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const currentRewards = userDoc.data().redeemedRewards || [];
      await updateDoc(userRef, {
        redeemedRewards: [...currentRewards, rewardId],
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error adding redeemed reward:', error);
    throw error;
  }
};

/**
 * Get user's redeemed rewards
 */
export const getUserRedeemedRewards = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().redeemedRewards || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching redeemed rewards:', error);
    throw error;
  }
};
